import { z } from "zod";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

export const client = "l1";
export const table_name = "l1_bank_positions";
export const portfolio_table_name = "l1_portfolios";
export const static_data_table_name = "l1_static_data";
export const includeClientFilter = false;
export const includeClientBankFields = false;

export const names = {
  portfolioId: "l1_portfolio_id",
  portfolioName: "l1_portfolio_name",
  portfolioClient: "l1_portfolio_client",
  portfolioBankName: "l1_portfolio_bank_name",
  portfolioVehicleContains: "l1_portfolio_vehicle_contains",
  portfolioClientContains: "l1_portfolio_client_contains",
  portfolioAccountNameContains: "l1_portfolio_account_name_contains",
  portfolioNameContains: "l1_portfolio_name_contains",
  portfolioBankNameContains: "l1_portfolio_bank_name_contains",
  portfolioCurrency: "l1_portfolio_currency",
  portfolioAccountType: "l1_portfolio_account_type",
  portfolioReportingCurrency: "l1_portfolio_reporting_currency",
  staticDatumId: "l1_static_datum_id",
  staticDatumName: "l1_static_datum_name",
  staticDatumNameContains: "l1_static_datum_name_contains",
  staticDataAssetClassEquals: "asset_class_1_equals",
  staticDataAssetSubclassEquals: "asset_class_2_equals",
  staticDatumCurrency: "l1_static_datum_currency",
  staticDatumAssetClass2: "l1_static_datum_asset_class_2",
} as const;

export type BankPositionRow = {
  id: number;
  date: string;
  quantity?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
  fixing_local_ccy?: number;
  fixing_ptf_ccy?: number;
  local_currency?: string;
  [key: string]: unknown;
};

export const filterSchema = z.object({
  [names.portfolioClientContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioVehicleContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioAccountNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioBankNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.staticDataAssetClassEquals]: z.string().optional(),
  [names.staticDataAssetSubclassEquals]: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  [names.staticDatumNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

export type FilterSchema = z.infer<typeof filterSchema>;
export type PortfolioOption = {
  text: string;
  id: number;
  account_type?: string | null;
  currency?: string | null;
  reporting_currency?: string | null;
};

export type AssetOption = {
  text: string;
  id: number;
  currency?: string | null;
  asset_class_2?: string | null;
};


const optionalNumber = z.preprocess(
  (value) => (value === "" || value == null ? null : value),
  z.coerce.number().nullable().optional(),
);

const optionalAutocompleteField = z
  .preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number().optional(),
      }),
    ]),
  )
  .optional()
  .nullable();

export const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  [names.portfolioName]: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string().nullable().optional(),
        currency: z.string().nullable().optional(),
        reporting_currency: z.string().nullable().optional(),
      }),
    ]),
  ),
  [names.staticDatumName]: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number(),
        currency: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      }),
    ]),
  ),
  [names.portfolioReportingCurrency]: z.string().nullable().optional(),
  [names.portfolioCurrency]: z.string().nullable().optional(),
  [names.staticDatumCurrency]: z.string().nullable().optional(),
  [names.portfolioAccountType]: z.string().nullable().optional(),
  [names.staticDatumAssetClass2]: z.string().nullable().optional(),
  client: optionalAutocompleteField,
  bank_name: optionalAutocompleteField,
  [names.portfolioId]: z.number(),
  [names.staticDatumId]: z.number(),
  market_value_local_ccy: optionalNumber,
  market_value_ptf_ccy: optionalNumber,
  fixing_local_ccy: optionalNumber,
  fixing_ptf_ccy: optionalNumber,
  quantity: optionalNumber,
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
});

type _BaseFormValues = z.infer<typeof FormSchema>;

export type TFormValues = Omit<
  _BaseFormValues,
  typeof names.portfolioName | typeof names.staticDatumName
> & {
  [K in typeof names.portfolioName]?: PortfolioOption | null;
} & {
  [K in typeof names.staticDatumName]?: AssetOption | null;
};
